import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Follow, type FollowEvent } from '../api';
import { useOpenDetail } from '../components/detail-link';
import { Chip, EmptyState, ErrorState, Loading, Notice, PageIntro, useAsync } from '../components/ui';
import { formatDateTime, relativeAge } from '../format';

/**
 * Followed topics. A follow whose subject changed since the previous visit is
 * marked; unchanged ones stay quiet. The previous visit is remembered per
 * browser (a convenience, not a record): without storage nothing is marked.
 */
const VISIT_KEY = 'hugarheim-follows-visited';

function readVisit(): number | null {
  try {
    const v = window.localStorage.getItem(VISIT_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

export function FollowsPage() {
  const [includeStopped, setIncludeStopped] = useState(false);
  const { data, error, loading, reload } = useAsync(() => api.follows(includeStopped), [includeStopped]);
  const [lastVisit] = useState(readVisit);

  useEffect(() => {
    try {
      window.localStorage.setItem(VISIT_KEY, String(Date.now()));
    } catch {
      /* no marking next time */
    }
  }, []);

  const all = data?.follows ?? [];
  // Story and card follows are checked by the follow monitor; subjects have
  // their own pages and are listed apart (ADR-032).
  const follows = all.filter((f) => f.subject_type !== 'entity');
  const subjects = all.filter((f) => f.subject_type === 'entity');
  const changed = (f: Follow) => lastVisit !== null && !!f.last_changed_at && Date.parse(f.last_changed_at) > lastVisit;
  const sorted = [...follows].sort((a, b) => {
    const ca = changed(a) ? 0 : 1;
    const cb = changed(b) ? 0 : 1;
    if (ca !== cb) return ca - cb;
    return (Date.parse(b.last_changed_at ?? b.created_at) || 0) - (Date.parse(a.last_changed_at ?? a.created_at) || 0);
  });
  const nChanged = follows.filter(changed).length;

  return (
    <>
      <PageIntro eyebrow="Verfolgt" title="Dranbleiben, ohne Rauschen.">
        Verfolgte Meldungen und deine Subjekte. Eine verfolgte Meldung wird auf wesentliche Änderungen geprüft; eine spätere, neue Meldung zum
        selben Thema gehört nicht dazu. Unternehmen, Behörden und Personen über alle Meldungen hinweg verfolgst du als Subjekt.
      </PageIntro>

      <section aria-labelledby="subjects-follow-h">
        <h2 id="subjects-follow-h">Subjekte</h2>
        {data && subjects.length === 0 && (
          <p className="muted">
            Keine Subjekte. <Link to="/subjekte">Subjekt anlegen</Link>
          </p>
        )}
        {subjects.length > 0 && (
          <ul className="plain">
            {subjects.map((f) => (
              <li key={f.id}>
                <Link to={`/subjekte/${f.subject_id}`}>{f.label}</Link>{' '}
                <Chip kind={f.status === 'active' ? 'ok' : 'neutral'}>{f.status === 'paused' ? 'pausiert' : f.status === 'active' ? 'aktiv' : 'beendet'}</Chip>
              </li>
            ))}
          </ul>
        )}
      </section>

      <h2>Verfolgte Meldungen</h2>

      <div className="toolbar">
        <p className="muted small" role="status">
          {data
            ? lastVisit === null
              ? `${follows.length} verfolgt.`
              : nChanged > 0
                ? `${nChanged} mit Änderungen seit deinem letzten Besuch.`
                : 'Keine Änderungen seit deinem letzten Besuch.'
            : ''}
        </p>
        <button type="button" className="small-button" aria-pressed={includeStopped} onClick={() => setIncludeStopped((v) => !v)}>
          Beendete anzeigen
        </button>
      </div>

      {loading && !data && <Loading />}
      {error && <ErrorState error={error} onRetry={reload} />}
      {data && follows.length === 0 && (
        <EmptyState title="Noch keine verfolgten Meldungen.">
          <p className="muted">
            Öffne eine Meldung und wähle „Meldung verfolgen“. Hier erscheint dann, wann sie zuletzt geprüft wurde und was sich geändert hat.
          </p>
        </EmptyState>
      )}
      {sorted.length > 0 && (
        <ul className="follow-list">
          {sorted.map((f) => (
            <FollowRow key={f.id} follow={f} isNew={changed(f)} onChanged={reload} />
          ))}
        </ul>
      )}
    </>
  );
}

function FollowRow({ follow, isNew, onChanged }: { follow: Follow; isNew: boolean; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<FollowEvent[] | null>(null);
  const [eventsError, setEventsError] = useState('');
  const [busy, setBusy] = useState(false);
  const openDetail = useOpenDetail();

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !events) {
      try {
        const res = await api.followEvents(follow.id);
        setEvents(res.events);
      } catch (err) {
        setEventsError(err instanceof Error ? err.message : 'Verlauf konnte nicht geladen werden');
      }
    }
  };

  return (
    <li className={`follow-row${isNew ? ' is-new' : ''}`}>
      <div className="spread">
        <h2 className="story-title">{follow.label}</h2>
        <span className="row">
          {isNew && <Chip kind="accent">Neu geändert</Chip>}
          <Chip kind={follow.status === 'active' ? 'ok' : 'neutral'}>{follow.status === 'active' ? 'aktiv' : 'beendet'}</Chip>
        </span>
      </div>
      <div className="meta">
        <span>Zuletzt geprüft: {follow.last_checked_at ? relativeAge(follow.last_checked_at) : 'noch nie'}</span>
        <span>
          Zuletzt geändert: {follow.last_changed_at ? formatDateTime(follow.last_changed_at) : 'keine Änderung seit Beginn'}
        </span>
        <span>{follow.subject_type === 'card' ? 'Training' : 'Meldung'}</span>
      </div>
      {follow.last_check_status === 'failed' && (
        <Notice kind="warn" title="Letzte Prüfung schlug fehl">
          {follow.last_check_error ?? 'Ohne Fehlermeldung.'} Änderungen können in dieser Zeit unbemerkt geblieben sein.
        </Notice>
      )}
      <div className="actions">
        <button type="button" className="small-button" aria-expanded={open} onClick={toggle}>
          {open ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
        </button>
        {follow.status === 'active' && (
          <button
            type="button"
            className="text-button danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.stopFollow(follow.id);
                onChanged();
              } finally {
                setBusy(false);
              }
            }}
          >
            Nicht mehr verfolgen
          </button>
        )}
      </div>
      {open && (
        <ul className="event-list">
          {eventsError && <li className="warn-text">{eventsError}</li>}
          {events === null && !eventsError && <li className="muted small">Lädt…</li>}
          {events?.length === 0 && <li className="muted small">Noch keine Ereignisse: seit Beginn keine inhaltliche Änderung.</li>}
          {events?.map((e) => (
            <li key={e.id}>
              <div className="spread">
                <span>
                  {e.kind === 'material_change' ? 'Wesentliche Änderung' : e.kind === 'check_failed' ? 'Prüfung fehlgeschlagen' : e.kind}
                  {e.card_version_id && (
                    <>
                      {' · '}
                      <button type="button" className="text-button" onClick={(ev) => openDetail(e.card_version_id!, ev.currentTarget)}>
                        Diese Version ansehen
                      </button>
                    </>
                  )}
                </span>
                <span className="muted small">{formatDateTime(e.created_at)}</span>
              </div>
              {e.detail?.reason && <div className="muted small">{e.detail.reason}</div>}
              {e.detail?.error && <div className="muted small">{e.detail.error}</div>}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
